import snakeCaseKeys from "snakecase-keys";
import ApiClient from "../client";
import {
  ApiArchiveFilterParams,
  ApiArchiveTimeSeriesData,
  ApiArchiveTimeSeriesParams,
  ApiChallenge,
  ApiCircuit,
  ApiCompetition,
  ApiLeague,
  ApiLine,
  ApiQuest,
  ApiRouteDetail,
  ApiRoutesFacets,
  ApiRouteStats,
  ApiUserVenueRole,
  ApiVenueAllStaffData,
  ApiVenueColor,
  ApiVenueDetail,
  ApiVenuePodiums,
  ApiVenueStaffInfo,
  ApiZone,
  PaginatedResponse,
} from "../types";

export const getVenue = (client: ApiClient, uuid: string) =>
  client.get<ApiVenueDetail>(`/venues/${uuid}`);

export const getVenues = (client: ApiClient, isStaff?: boolean) =>
  client.get<PaginatedResponse<ApiVenueDetail>>("/venues", {
    searchParams: {
      ...(isStaff !== undefined ? { is_staff: isStaff } : {}),
    },
  });

export const getVenueRoutes = (
  client: ApiClient,
  uuid: string,
  { competitionUuid }: { competitionUuid?: string } | undefined = {}
) =>
  client.get<PaginatedResponse<ApiRouteDetail>>(`/venues/${uuid}/routes`, {
    searchParams: competitionUuid
      ? { competition: competitionUuid }
      : undefined,
  });

export const getVenueCircuits = (client: ApiClient, uuid: string) =>
  client.get<PaginatedResponse<ApiCircuit>>(`/venues/${uuid}/circuits`);

export const getVenueLines = (client: ApiClient, uuid: string) =>
  client.get<PaginatedResponse<ApiLine>>(`/venues/${uuid}/lines`);

export const getVenueZones = (client: ApiClient, uuid: string) =>
  client.get<PaginatedResponse<ApiZone>>(`/venues/${uuid}/zones`);

export const getVenueStaff = (client: ApiClient, uuid: string) =>
  client.get<PaginatedResponse<ApiVenueStaffInfo>>(`/venues/${uuid}/staff`);

export const getVenueColors = (client: ApiClient, uuid: string) =>
  client.get<PaginatedResponse<ApiVenueColor>>(`/venues/${uuid}/colors`);

export const deleteVenueColor = (
  client: ApiClient,
  uuid: string,
  colorUuid: string
) => client.delete(`/venues/${uuid}/colors/${colorUuid}`);

export const exportVenueRoutes = (
  client: ApiClient,
  uuid: string,
  routeUuids: string[]
) =>
  client.post(
    `/venues/${uuid}/routes/export`,
    { routeUuids },
    { returnResponse: true }
  );

export type MapPdfPayload =
  | {
      circuit: string;
    }
  | {
      routes: string[];
      name?: string | null;
      description?: string | null;
    };

export const generateMapPdf = (
  client: ApiClient,
  uuid: string,
  params: MapPdfPayload
) => client.post<{ url: string }>(`/venues/${uuid}/generate-map`, params);

export const generateSportCardsPdf = (
  client: ApiClient,
  uuid: string,
  params: ({ lines: string[] } | { routes: string[] }) & {
    cardSize?: "94x120" | "3x4";
    pageSize?: "A4" | "letter";
  }
) =>
  client.post<{ url: string }>(`/venues/${uuid}/generate-sport-cards`, params);

export type CardSize = "A5" | "A6";

export const generateLineCardsPdf = (
  client: ApiClient,
  uuid: string,
  {
    lines,
    isBasic,
    cardSize,
  }: { lines: string[]; isBasic?: boolean; cardSize?: CardSize }
) =>
  client.post<{ url: string }>(`/venues/${uuid}/generate-line-card`, {
    lines,
    isBasic: isBasic || false,
    cardSize,
  });

export const getVenueRoutesStats = (client: ApiClient, uuid: string) =>
  client.get<ApiRouteStats[]>(`/venues/${uuid}/routes/stats`);

export const getVenueCompetitions = (client: ApiClient, uuid: string) =>
  client.get<PaginatedResponse<ApiCompetition>>(`/venues/${uuid}/competitions`);

export const getVenueLeagues = (client: ApiClient, uuid: string) =>
  client.get<PaginatedResponse<ApiLeague>>(`/venues/${uuid}/leagues`);

export const getVenueChallenges = (client: ApiClient, uuid: string) =>
  client.get<PaginatedResponse<ApiChallenge>>(`/venues/${uuid}/challenges`);

export const getVenueQuests = (client: ApiClient, uuid: string) =>
  client.get<PaginatedResponse<ApiQuest>>(`/venues/${uuid}/quests`);

export const getAllVenuesStaff =(client: ApiClient, venues?: string[]) => {
  let url = `/venues/all-staff`;
  if (venues) {
    url += "?" + venues.map((v) => `venue=${v}`).join("&");
  }
  return client.get<ApiVenueAllStaffData>(url);
};

export const updateStaffRole = (
  client: ApiClient,
  venueUuid: string,
  userUuid: string,
  payload: { role?: string; isActive?: boolean }
) =>
  client.patch<ApiUserVenueRole>(
    `/venues/${venueUuid}/staff/${userUuid}`,
    payload
  );

export const updateAllUserRoles = (
  client: ApiClient,
  userUuid: string,
  payload: { role?: string; isActive?: boolean; venues?: string[] }
) => client.patch(`/venues/all-staff/${userUuid}`, payload);

function filterParamsToURLSearchParams<T extends Record<string, unknown>>(
  data: T
) {
  const params = new URLSearchParams();

  Object.entries(snakeCaseKeys(data)).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((val) => params.append(key, val.toString()));
    } else {
      params.append(key, (value ?? "").toString());
    }
  });

  return params;
}

export const getVenueArchiveRoutes = (
  client: ApiClient,
  uuid: string,
  params: { page?: number } & ApiArchiveFilterParams
) =>
  client.get<PaginatedResponse<ApiRouteDetail>>(
    `/venues/${uuid}/routes/archive`,
    {
      searchParams: filterParamsToURLSearchParams(params),
    }
  );

export const getVenueArchiveRoutesFacets = (
  client: ApiClient,
  uuid: string,
  filterParams: ApiArchiveFilterParams
) =>
  client.get<ApiRoutesFacets>(`/venues/${uuid}/routes/archive/facets`, {
    searchParams: filterParamsToURLSearchParams(filterParams),
  });

export const getVenueArchiveRoutesTimeSeries = (
  client: ApiClient,
  uuid: string,
  params: ApiArchiveTimeSeriesParams,
  filterParams?: ApiArchiveFilterParams
) =>
  client.get<ApiArchiveTimeSeriesData>(
    `/venues/${uuid}/routes/archive/timeseries`,
    {
      searchParams: filterParamsToURLSearchParams({
        ...filterParams,
        ...params,
      }),
    }
  );

/** `day` (yyyy-MM-dd) picks the month; it defaults to the venue's current month. */
export const getVenuePodiums = (
  client: ApiClient,
  uuid: string,
  { day }: { day?: string } | undefined = {}
) =>
  client.get<ApiVenuePodiums>(`/venues/${uuid}/scoreboards/podiums`, {
    searchParams: day ? { day } : undefined,
  });

/**
 * Bans a user from the venue's leaderboard for the current month, which also
 * disqualifies them from any points quest running there now. Admins only.
 */
export const banFromLeaderboard = (
  client: ApiClient,
  uuid: string,
  userUuid: string
) => client.post<void>(`/venues/${uuid}/scoreboards/bans`, { user: userUuid });

export const endpoints = {
  getVenues,
  getVenue,
  getVenueRoutes,
  getVenueRoutesStats,
  getVenueCircuits,
  getVenueLines,
  getVenueZones,
  getVenueStaff,
  getVenueColors,
  deleteVenueColor,
  getVenueCompetitions,
  getVenueChallenges,
  getVenueQuests,
  getVenuePodiums,
  banFromLeaderboard,
  getVenueLeagues,
  exportVenueRoutes,
  generateMapPdf,
  generateSportCardsPdf,
  generateLineCardsPdf,
  getAllVenuesStaff,
  updateStaffRole,
  updateAllUserRoles,
  getVenueArchiveRoutes,
  getVenueArchiveRoutesFacets,
  getVenueArchiveRoutesTimeSeries,
};
